import { gpCommon } from '../app';
import { IStorageService } from './scoped-storage';

// Re-exported so the long-standing `from './local-storage-service'` imports of
// ScopedStorage and friends keep resolving after the split.
export * from './scoped-storage';

export interface ISessionStorageService {
    session_getAsString(key: string): string | null;
    session_setAsString(key: string, value: string): void;

    session_getAsObject<T>(key: string): T | null;
    session_setAsObject<T>(key: string, value: T): void;

    session_remove(key: string): void;
}

class StorageService implements IStorageService {

    public constructor(protected storage: Storage) {
    }

    getAsString(key: string) {
        return this.storage.getItem(key);
    }

    setAsString(key: string, value: string): void {
        this.storage.setItem(key, value);
    }

    getAsObject<T>(key: string): T | null {

        var json = this.getAsString(key);

        if (json === null) return null;
        var obj = JSON.parse(json);
        return obj;
    }

    setAsObject<T>(key: string, obj: T): void {
        var json = JSON.stringify(obj);
        this.setAsString(key, json);
    }

    remove(key: string): void {
        this.storage.removeItem(key);
    }

    keys(): string[] {
        // Snapshotted, not lazy: Storage reindexes on every write, so a caller
        // removing entries as it walks would skip the ones that shuffled down.
        const result: string[] = [];
        for (let i = 0; i < this.storage.length; i++) {
            const key = this.storage.key(i);
            if (key !== null) result.push(key);
        }
        return result;
    }
}

class SessionStorageService implements ISessionStorageService {

    public constructor(protected storage: Storage) {
    }

    session_getAsString(key: string): string | null {
        return this.storage.getItem(key);
    }

    session_setAsString(key: string, value: string): void {
        this.storage.setItem(key, value);
    }

    session_getAsObject<T>(key: string): T | null {

        var json = this.session_getAsString(key);

        if (json === null) return null;
        try {
            var obj = JSON.parse(json);
            return obj;
        } catch {
            return null;
        }
    }

    session_setAsObject<T>(key: string, obj: T): void {
        var json = JSON.stringify(obj);
        this.session_setAsString(key, json);
    }

    session_remove(key: string): void {
        this.storage.removeItem(key);
    }
}

class StorageWithVersionNumber extends StorageService {

    private current_storage_version = "v2";
    private storage_version_key = "gameplan.storage.version";

    constructor(storage: Storage) {
        super(storage);

        if (this.getAsString(this.storage_version_key) !== this.current_storage_version) {
            this.storage.clear();
            this.setAsString(this.storage_version_key, this.current_storage_version);
        }
    }

}


class SessionStorageWithVersionNumber extends SessionStorageService {

    private current_storage_version = "v3";
    private storage_version_key = "gameplan.storage.version";

    constructor(storage: Storage) {
        super(storage);

        if (this.session_getAsString(this.storage_version_key) !== this.current_storage_version) {
            this.storage.clear();
            this.session_setAsString(this.storage_version_key, this.current_storage_version);
        }
    }

}

export function gpLocalStorageServiceFactory(): IStorageService {
    return new StorageWithVersionNumber(localStorage);
}

declare module 'angular' {
  namespace auto {
    interface IInjectorService {
      get(name: 'gpLocalStorageService'): IStorageService;
      get(name: 'gpSessionStorageService'): ISessionStorageService;
    }
  }
}


gpCommon.factory('gpLocalStorageService', [function (): IStorageService {
    return new StorageWithVersionNumber(localStorage);
}]);

gpCommon.factory('gpSessionStorageService', [function (): ISessionStorageService {
    return new SessionStorageWithVersionNumber(sessionStorage);
}]);
